import React, { useState } from 'react';
import { BookOpen, GraduationCap, Settings, Search, Filter, Users, FileText, Video, HelpCircle, ArrowRight, Clock, User, Globe } from 'lucide-react';
import { SOMAChatInterface } from './SOMAChatInterface';
import { TylerCommunityBrowser, FEATURE_EMBED_COMMUNITY } from '../../SupportBridge/Resources/TylerCommunityBrowser';

interface KnowledgeCategory {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  articleCount: number;
  authorCount: number;
  color: string;
  articles?: KnowledgeArticle[];
}

interface KnowledgeArticle {
  id: string;
  title: string;
  description: string;
  category: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  readTime: number;
  author: string;
  lastUpdated: string;
  tags: string[];
  views: number;
}

const knowledgeCategories: KnowledgeCategory[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    description: 'Essential guides to begin your Tyler Technologies journey',
    icon: BookOpen,
    articleCount: 12,
    authorCount: 4,
    color: 'bg-gradient-to-r from-[#4267A1] to-[#5A7BC4]',
    articles: [
      {
        id: 'gs-1',
        title: 'Tyler Technologies Platform Overview',
        description: 'Comprehensive introduction to the Tyler Technologies ecosystem and core features',
        category: 'getting-started',
        difficulty: 'Beginner',
        readTime: 8,
        author: 'Sarah Johnson',
        lastUpdated: '2024-01-15',
        tags: ['overview', 'platform', 'basics'],
        views: 1247
      },
      {
        id: 'gs-2',
        title: 'Initial System Setup and Configuration',
        description: 'Step-by-step guide to configure your Tyler Technologies environment',
        category: 'getting-started',
        difficulty: 'Beginner',
        readTime: 12,
        author: 'Mike Chen',
        lastUpdated: '2024-01-10',
        tags: ['setup', 'configuration', 'installation'],
        views: 892
      }
    ]
  },
  {
    id: 'user-management',
    title: 'User Management',
    description: 'Manage users, roles, and permissions effectively',
    icon: Users,
    articleCount: 8,
    authorCount: 3,
    color: 'bg-gradient-to-r from-[#8A9150] to-[#A4B366]',
    articles: [
      {
        id: 'um-1',
        title: 'Creating and Managing User Accounts',
        description: 'Complete guide to user account creation and management',
        category: 'user-management',
        difficulty: 'Intermediate',
        readTime: 10,
        author: 'David Park',
        lastUpdated: '2024-01-12',
        tags: ['users', 'accounts', 'management'],
        views: 634
      }
    ]
  },
  {
    id: 'integrations',
    title: 'Integrations',
    description: 'Connect Tyler Technologies with external systems',
    icon: Settings,
    articleCount: 15,
    authorCount: 5,
    color: 'bg-gradient-to-r from-[#4267A1] to-[#8A9150]',
    articles: [
      {
        id: 'int-1',
        title: 'API Integration Fundamentals',
        description: 'Learn the basics of integrating with Tyler Technologies APIs',
        category: 'integrations',
        difficulty: 'Intermediate',
        readTime: 20,
        author: 'Alex Thompson',
        lastUpdated: '2024-01-14',
        tags: ['api', 'integration', 'development'],
        views: 892
      }
    ]
  },
  {
    id: 'troubleshooting',
    title: 'Troubleshooting',
    description: 'Resolve common issues and technical problems',
    icon: HelpCircle,
    articleCount: 20,
    authorCount: 6,
    color: 'bg-gradient-to-r from-[#DC2626] to-[#EF4444]',
    articles: [
      {
        id: 'ts-1',
        title: 'Common Login and Authentication Issues',
        description: 'Troubleshoot login problems and authentication failures',
        category: 'troubleshooting',
        difficulty: 'Beginner',
        readTime: 8,
        author: 'Robert Kim',
        lastUpdated: '2024-01-13',
        tags: ['login', 'authentication', 'troubleshooting'],
        views: 1156
      }
    ]
  },
  {
    id: 'training-videos',
    title: 'Training Videos',
    description: 'Video tutorials and training materials',
    icon: Video,
    articleCount: 25,
    authorCount: 8,
    color: 'bg-gradient-to-r from-[#7C3AED] to-[#A855F7]',
    articles: [
      {
        id: 'tv-1',
        title: 'Tyler Technologies Platform Walkthrough',
        description: 'Complete video tour of the Tyler Technologies platform',
        category: 'training-videos',
        difficulty: 'Beginner',
        readTime: 45,
        author: 'Training Team',
        lastUpdated: '2024-01-16',
        tags: ['video', 'walkthrough', 'platform'],
        views: 2134
      }
    ]
  },
  {
    id: 'best-practices',
    title: 'Best Practices',
    description: 'Industry standards and recommended approaches',
    icon: GraduationCap,
    articleCount: 18,
    authorCount: 7,
    color: 'bg-gradient-to-r from-[#059669] to-[#10B981]',
    articles: [
      {
        id: 'bp-1',
        title: 'Security Best Practices',
        description: 'Essential security guidelines for Tyler Technologies implementations',
        category: 'best-practices',
        difficulty: 'Intermediate',
        readTime: 22,
        author: 'Security Team',
        lastUpdated: '2024-01-15',
        tags: ['security', 'best-practices', 'guidelines'],
        views: 1089
      }
    ]
  }
];

export const KnowledgeView: React.FC = () => {
  const [activeView, setActiveView] = useState<'categories' | 'articles' | 'article' | 'chat' | 'community'>('categories');
  const [selectedCategory, setSelectedCategory] = useState<KnowledgeCategory | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<KnowledgeArticle | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showChatInterface, setShowChatInterface] = useState(false);

  const handleCategoryClick = (category: KnowledgeCategory) => {
    setSelectedCategory(category);
    setActiveView('articles');
  };

  const handleArticleClick = (article: KnowledgeArticle) => {
    setSelectedArticle(article);
    setActiveView('article');
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setSelectedArticle(null);
    setActiveView('categories');
  };

  const handleBackToArticles = () => {
    setSelectedArticle(null);
    setActiveView('articles');
  };

  const handleAskQuestion = () => {
    setShowChatInterface(true);
    setActiveView('chat');
  };

  const handleOpenCommunity = () => {
    setActiveView('community');
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Beginner': return 'bg-green-100 text-green-800';
      case 'Intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'Advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="h-full bg-white">
      <div className="h-full flex flex-col">
        <div className="p-8 border-b border-gray-200">
          <div className="mb-6">
            <h2 className="text-3xl font-bold bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] bg-clip-text text-transparent mb-4 drop-shadow-sm">
              Learning & Resources
            </h2>
            <p className="text-gray-600 text-lg drop-shadow-sm">
              Find answers, guides, and training resources for your Tyler Technologies implementation
            </p>
          </div>
          
          <div className="relative max-w-2xl mb-6">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search for articles, guides, or ask a question..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#4267A1] focus:border-[#4267A1] text-lg"
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button 
                onClick={handleAskQuestion}
                className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] text-white rounded-xl font-medium hover:shadow-lg hover:shadow-[#4267A1]/25 transition-all duration-200"
              >
                <HelpCircle className="w-5 h-5" />
                <span>Ask SOMA</span>
              </button>
              
              {FEATURE_EMBED_COMMUNITY && (
                <button 
                  onClick={handleOpenCommunity}
                  className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-[#8A9150] to-[#A5B366] text-white rounded-xl font-medium hover:shadow-lg hover:shadow-[#8A9150]/25 transition-all duration-200"
                >
                  <Globe className="w-5 h-5" />
                  <span>Tyler Community</span>
                </button>
              )}
              
              <div className="flex items-center space-x-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <select className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4267A1] focus:border-[#4267A1]">
                  <option value="all">All Levels</option>
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
                
                <select className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4267A1] focus:border-[#4267A1]">
                  <option value="all">All Types</option>
                  <option value="article">Articles</option>
                  <option value="video">Videos</option>
                  <option value="guide">Guides</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-hidden">
          {activeView === 'categories' && (
            <div className="h-full overflow-y-auto p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {knowledgeCategories.map((category) => (
                  <div
                    key={category.id}
                    onClick={() => handleCategoryClick(category)}
                    className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg hover:border-[#4267A1]/30 transition-all duration-200 cursor-pointer group"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className={`p-3 rounded-lg ${category.color} group-hover:scale-110 transition-transform duration-200`}>
                        <category.icon className="w-6 h-6 text-white" />
                      </div>
                      <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-[#4267A1] transition-colors duration-200" />
                    </div>
                    
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-[#4267A1] transition-colors duration-200 drop-shadow-sm">
                      {category.title}
                    </h3>
                    
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2 drop-shadow-sm">
                      {category.description}
                    </p>
                    
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <div className="flex items-center space-x-4">
                        <span className="flex items-center space-x-1">
                          <FileText className="w-4 h-4" />
                          <span>{category.articleCount} articles</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <Users className="w-4 h-4" />
                          <span>{category.authorCount} authors</span>
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeView === 'articles' && selectedCategory && (
            <div className="h-full overflow-y-auto p-8">
              <div className="mb-6">
                <button
                  onClick={handleBackToCategories}
                  className="flex items-center space-x-2 text-[#4267A1] hover:text-[#5A7BC4] transition-colors duration-200 mb-4"
                >
                  <ArrowRight className="w-4 h-4 rotate-180" />
                  <span>Back to Categories</span>
                </button>
                
                <div className="flex items-center space-x-3 mb-2">
                  <div className={`p-2 rounded-lg ${selectedCategory.color}`}>
                    <selectedCategory.icon className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">{selectedCategory.title}</h2>
                </div>
                
                <p className="text-gray-600">{selectedCategory.description}</p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {selectedCategory.articles?.map((article) => (
                  <div
                    key={article.id}
                    onClick={() => handleArticleClick(article)}
                    className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md hover:border-[#4267A1]/30 transition-all duration-200 cursor-pointer"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2 hover:text-[#4267A1] transition-colors duration-200 drop-shadow-sm">
                          {article.title}
                        </h3>
                        
                        <p className="text-gray-600 text-sm mb-3 line-clamp-2 drop-shadow-sm">
                          {article.description}
                        </p>
                        
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(article.difficulty)}`}>
                            {article.difficulty}
                          </span>
                          
                          <span className="flex items-center space-x-1">
                            <Clock className="w-4 h-4" />
                            <span>{article.readTime} min read</span>
                          </span>
                          
                          <span className="flex items-center space-x-1">
                            <User className="w-4 h-4" />
                            <span>{article.author}</span>
                          </span>
                          
                          <span>{article.views} views</span>
                        </div>
                        
                        <div className="flex flex-wrap gap-2 mt-3">
                          {article.tags.map((tag, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      <ArrowRight className="w-5 h-5 text-gray-400 ml-4" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeView === 'article' && selectedArticle && (
            <div className="h-full overflow-y-auto p-8">
              <div className="max-w-4xl mx-auto">
                <button
                  onClick={handleBackToArticles}
                  className="flex items-center space-x-2 text-[#4267A1] hover:text-[#5A7BC4] transition-colors duration-200 mb-6"
                >
                  <ArrowRight className="w-4 h-4 rotate-180" />
                  <span>Back to Articles</span>
                </button>
                
                <div className="bg-white border border-gray-200 rounded-xl p-8">
                  <h1 className="text-3xl font-bold text-gray-900 mb-4">{selectedArticle.title}</h1>
                  
                  <div className="flex items-center space-x-6 text-sm text-gray-500 mb-6">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getDifficultyColor(selectedArticle.difficulty)}`}>
                      {selectedArticle.difficulty}
                    </span>
                    
                    <span className="flex items-center space-x-1">
                      <Clock className="w-4 h-4" />
                      <span>{selectedArticle.readTime} min read</span>
                    </span>
                    
                    <span className="flex items-center space-x-1">
                      <User className="w-4 h-4" />
                      <span>{selectedArticle.author}</span>
                    </span>
                    
                    <span>Last updated: {selectedArticle.lastUpdated}</span>
                    <span>{selectedArticle.views} views</span>
                  </div>
                  
                  <div className="prose max-w-none">
                    <p className="text-lg text-gray-700 mb-6">{selectedArticle.description}</p>
                    
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Article Content</h3>
                      <p className="text-gray-600">
                        This is where the full article content would be displayed. The article covers {selectedArticle.title.toLowerCase()} 
                        and provides comprehensive guidance for Tyler Technologies users.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 mt-6">
                    {selectedArticle.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-[#4267A1]/10 text-[#4267A1] text-sm rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeView === 'chat' && (
            <div className="h-full">
              <SOMAChatInterface
                context="knowledge"
                initialQuery={searchQuery}
                onClose={() => {
                  setShowChatInterface(false);
                  setActiveView('categories');
                }}
              />
            </div>
          )}

          {activeView === 'community' && FEATURE_EMBED_COMMUNITY && (
            <div className="h-full p-8">
              <div className="mb-6">
                <button
                  onClick={() => setActiveView('categories')}
                  className="flex items-center space-x-2 text-[#4267A1] hover:text-[#5A7BC4] transition-colors duration-200 mb-4"
                >
                  <ArrowRight className="w-4 h-4 rotate-180" />
                  <span>Back to Learning & Resources</span>
                </button>
              </div>
              <TylerCommunityBrowser />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
